import { getApiHost } from '@/appSettings';
import { useLoginStore } from '@/login/stores/useLoginStore';
import type { CopilotChangedEmploymentsResponse, CopilotChangedGroup, CopilotKeyValuesResponse, CopilotNewEmploymentsResponse, CopilotNewGroup } from '@sysarb/sysarb-types-v2/services/v1/job-architecture';
import type { QueryOptions, UseMutationOptions } from '@tanstack/vue-query';
import { queryOptions } from '@tanstack/vue-query';
import { RestManager } from '@/newApi/services/rest/RestManager';
import type { ConfirmChangeInput, ObservedFields } from '@sysarb/sysarb-types-v2/services/v1/job';
import type { Model} from '@/newApi/services/ResgateManager';
import { ResErr, ResgateManager } from '@/newApi/services/ResgateManager';
import type { StorageFilterGroup } from '@sysarb/sysarb-types-v2/services/v1/filter-storage';
import type { BaseRegisterTableInput, TableOut } from '@sysarb/sysarb-types-v2/services/v1/base-registry';
import type { Direction, InputFieldNames } from '@sysarb/sysarb-types-v2/services/v1/analytics';
import { DataSources } from '@sysarb/sysarb-types-v2/services/v1/analytics';

const jobService = new RestManager(`https://${getApiHost()}/job-service`);
const baseRegistryService = new RestManager(`https://${getApiHost()}/base-registry-service`);

export type GetEmploymentsParams = {
    filter: StorageFilterGroup;
    fields: InputFieldNames[];
    orderBy: InputFieldNames;
    orderDirection: Direction;
    page: number;
    pageSize: number;
    groupBy?: {
        field: InputFieldNames;
        // undefined = get groups, string = get rows for specific group value, null = get rows where field IS NULL
        getRowsForGroup?: string | null;
        direction: Direction;
    };
};

type JobEmploymentsOptions = {
    queries: {
        getEmployments: (params: GetEmploymentsParams) => QueryOptions<TableOut, Error>;
        getCopilotKeyValues: () => QueryOptions<CopilotKeyValuesResponse | null, Error>,
        getCopilotNewGroups: () => QueryOptions<CopilotNewGroup[] | null, Error>,
        getCopilotNewGroupEmployments: (copilotNewGroupId?: string | null, queries?: { page?: number, pageSize?: number, orderBy?: string, orderDirection?: 'asc' | 'desc' }) => QueryOptions<CopilotNewEmploymentsResponse | null, Error>,
        getCopilotNewGroupEmploymentIds: (copilotNewGroupId: string | null) => QueryOptions<{ ids: string[] } | null, Error>,
        getCopilotChangedGroups: () => QueryOptions<CopilotChangedGroup[] | null, Error>,
        getCopilotChangedGroupEmployments: (copilotChangedGroupId?: string | null, queries?: { page?: number, pageSize?: number, orderBy?: string, orderDirection?: 'asc' | 'desc' }) => QueryOptions<CopilotChangedEmploymentsResponse | null, Error>,
        getCopilotChangedGroupEmploymentIds: (copilotChangedGroupId: string | null) => QueryOptions<{ ids: string[] } | null, Error>,
        getCopilotObserverFields: (subscribe?: boolean) => QueryOptions<Model<ObservedFields> | null, Error, ObservedFields>,
    },
    mutations: {
        assignEmploymentToJobProfile: UseMutationOptions<string, Error, ConfirmChangeInput>,
    }
};

export const jobEmployments: JobEmploymentsOptions = {
    queries: {
        getEmployments: (params: GetEmploymentsParams): QueryOptions<TableOut, Error> => {
            const { instance } = useLoginStore();

            return queryOptions<TableOut, Error>({
                queryKey: ['employments', instance, params],
                queryFn: async (): Promise<TableOut> => {
                    const input: BaseRegisterTableInput = {
                        filter: params.filter,
                        fields: params.fields,
                        datasource: DataSources.BaseRegisterEmployments,
                        orderBy: params.orderBy,
                        orderDirection: params.orderDirection,
                        page: params.page,
                        pageSize: params.pageSize,
                        groupBy: params.groupBy,
                    };

                    try {
                        return await baseRegistryService.post<TableOut>(input, `v1/${instance}/base-register/filter`);
                    } catch (error) {
                        console.error('getEmployments', error);
                        throw error;
                    }
                }
            });
        },
        getCopilotKeyValues: (): QueryOptions<CopilotKeyValuesResponse | null, Error> => {
            const { instance } = useLoginStore();
            const url = `v1/${instance}/copilot/key-values`;
            return queryOptions<CopilotKeyValuesResponse | null, Error>({
                queryKey: [url],
                queryFn: async (): Promise<CopilotKeyValuesResponse | null> => {
                    try {
                        return await jobService.get<CopilotKeyValuesResponse>(url);
                    } catch (error) {
                        console.error('getCopilotKeyValues', error);
                        throw error;
                    }
                }
            });
        },
        getCopilotNewGroups: (): QueryOptions<CopilotNewGroup[] | null, Error> => {
            const { instance } = useLoginStore();
            return queryOptions<CopilotNewGroup[] | null, Error>({
                queryKey: ['copilotNewGroups', instance],
                queryFn: async (): Promise<CopilotNewGroup[] | null> => {
                    try {
                        return await jobService.get<CopilotNewGroup[]>(`v1/${instance}/copilot/new/groups`);
                    } catch (error) {
                        console.error('getCopilotNewGroups', error);
                        throw error;
                    }
                }
            });
        },
        getCopilotNewGroupEmployments: (copilotNewGroupId?: string | null, queries?: { page?: number, pageSize?: number, orderBy?: string, orderDirection?: 'asc' | 'desc' }): QueryOptions<CopilotNewEmploymentsResponse | null, Error> => {
            const { instance } = useLoginStore();
            const queryString = new URLSearchParams();

            return queryOptions<CopilotNewEmploymentsResponse | null, Error>({
                queryKey: [instance, copilotNewGroupId, queries, queryString],
                queryFn: async (): Promise<CopilotNewEmploymentsResponse | null> => {
                    if (queries?.page != null) queryString.set('page', queries.page.toString());
                    if (queries?.pageSize != null) queryString.set('pageSize', queries.pageSize.toString());
                    if (queries?.orderBy != null) queryString.set('orderBy', queries.orderBy);
                    if (queries?.orderDirection != null) queryString.set('orderDirection', queries.orderDirection);
                    const url = `v1/${instance}/copilot/new/group/${copilotNewGroupId}/employments${queryString.toString() ? `?${queryString.toString()}` : ''}`;
                    if (copilotNewGroupId == null) return null;
                    try {
                        return await jobService.get<CopilotNewEmploymentsResponse>(url);
                    } catch (error) {
                        console.error('getCopilotNewGroupEmployments', error);
                        throw error;
                    }
                }
            });
        },
        getCopilotChangedGroups: (): QueryOptions<CopilotChangedGroup[] | null, Error> => {
            const { instance } = useLoginStore();
            return queryOptions<CopilotChangedGroup[] | null, Error>({
                queryKey: ['copilotChangedGroups', instance],
                queryFn: async (): Promise<CopilotChangedGroup[] | null> => {
                    try {
                        return await jobService.get<CopilotChangedGroup[]>(`v1/${instance}/copilot/changed/groups`);
                    } catch (error) {
                        console.error('getCopilotChangedGroups', error);
                        throw error;
                    }
                }
            });
        },
        getCopilotChangedGroupEmployments: (copilotChangedGroupId?: string | null, queries?: { page?: number, pageSize?: number, orderBy?: string, orderDirection?: 'asc' | 'desc' }): QueryOptions<CopilotChangedEmploymentsResponse | null, Error> => {
            const { instance } = useLoginStore();
            const queryString = new URLSearchParams();

            return queryOptions<CopilotChangedEmploymentsResponse | null, Error>({
                queryKey: [instance, copilotChangedGroupId, queries, queryString],
                queryFn: async (): Promise<CopilotChangedEmploymentsResponse | null> => {
                    if (queries?.page != null) queryString.set('page', queries.page.toString());
                    if (queries?.pageSize != null) queryString.set('pageSize', queries.pageSize.toString());
                    if (queries?.orderBy != null) queryString.set('orderBy', queries.orderBy);
                    if (queries?.orderDirection != null) queryString.set('orderDirection', queries.orderDirection);
                    const url = `v1/${instance}/copilot/changed/group/${copilotChangedGroupId}/employments${queryString.toString() ? `?${queryString.toString()}` : ''}`;
                    if (copilotChangedGroupId == null) return null;
                    try {
                        return await jobService.get<CopilotChangedEmploymentsResponse>(url);
                    } catch (error) {
                        console.error('getCopilotNewGroupEmployments', error);
                        throw error;
                    }
                }
            });
        },
        getCopilotNewGroupEmploymentIds: (copilotNewGroupId: string | null): QueryOptions<{ ids: string[] } | null, Error> => {
            const { instance } = useLoginStore();
            return queryOptions<{ ids: string[] } | null, Error>({
                queryKey: ['copilotNewGroupEmploymentIds', instance, copilotNewGroupId],
                queryFn: async (): Promise<{ ids: string[] } | null> => {
                    if (copilotNewGroupId == null) return null;
                    try {
                        return await jobService.get<{ ids: string[] }>(`v1/${instance}/copilot/new/group/${copilotNewGroupId}/employments/ids`);
                    } catch (error) {
                        console.error('getCopilotNewGroupEmploymentIds', error);
                        throw error;
                    }
                },
                enabled: () => copilotNewGroupId != null,
            });
        },
        getCopilotChangedGroupEmploymentIds: (copilotChangedGroupId: string | null): QueryOptions<{ ids: string[] } | null, Error> => {
            const { instance } = useLoginStore();
            return queryOptions<{ ids: string[] } | null, Error>({
                queryKey: ['copilotChangedGroupEmploymentIds', instance, copilotChangedGroupId],
                queryFn: async (): Promise<{ ids: string[] } | null> => {
                    if (copilotChangedGroupId == null) return null;
                    try {
                        return await jobService.get<{ ids: string[] }>(`v1/${instance}/copilot/changed/group/${copilotChangedGroupId}/employments/ids`);
                    } catch (error) {
                        console.error('getCopilotChangedGroupEmploymentIds', error);
                        throw error;
                    }
                },
                enabled: () => copilotChangedGroupId != null,
            });
        },
        getCopilotObserverFields: (subscribe: boolean = true): QueryOptions<Model<ObservedFields> | null, Error, ObservedFields> => {
            const { instanceTopic } = useLoginStore();
            const topic = `v1.${instanceTopic}.observedfields`;
            return queryOptions<Model<ObservedFields> | null, Error, ObservedFields>({
                meta: {
                    subscribe,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryKey: ['jobs', topic],
                queryFn: async (): Promise<Model<ObservedFields> | null> => {
                    return ResgateManager.model<ObservedFields>(topic).then(res => {
                        if (res instanceof ResErr) {
                            throw new Error(res.message);
                        }
                        return res;
                    });
                },
            });
        },
    },
    mutations: {
        assignEmploymentToJobProfile: {
            mutationFn: async (payload: ConfirmChangeInput): Promise<string> => {
                const { instance } = useLoginStore();
                const result = await jobService.post<string>(payload, `v1/${instance}/job-employments/confirm-job-change`);
                return result;
            }
        }
    }
};
